/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { getMergedIpr } from '@upright/common/model/input/companies/products/product-input-ui/utils';
import { useState } from 'react';
import { Dialog, Button, SegmentedControl, CounterLabel } from '@primer/react';
import styled from 'styled-components';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { BasicProperties } from './basic-properties';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { getAttributeWarnings } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';
import { mergeIprAttributes } from '@upright/common/model/input/companies/products/ipr/merge-ipr-attributes';
import { getAttributesCount, getAdvancedAttributesCount } from './helpers';
import { getVisibleProductEditDialogTabs, ProductEditDialogTab } from './dialog-tabs';
import { AttributesSection, AdvancedAttributesSection } from './attributes-sections';
import { InheritedAttributesHint } from './inherited-attributes-hint';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { AppcuesEventId } from '@upright/ui/integrations/appcues/types';
import { TrackingId } from '@upright/ui/integrations/tracking-ids';
import { trackAppcuesEvent } from '@upright/ui/integrations/appcues';
import { MatchingSection } from './matching-section';
import { useTabGuidance } from './tab-guidance';
import { useHasProductAttributeAccess } from '../product-attribute-access';

interface ProductEditDialogProps {
  onClose: () => void;
  product: PiuProductState;
  onSave: (product: PiuProductState) => void;
  mode: 'edit' | 'create';
  companyId?: string;
  /**
   * Gated segment editor (PLT-5707): drop the "Matching" tab, which is team-only there and has its
   * own "Edit matching" modal on the row kebab, and the revenue field, which belongs to the
   * Hierarchy view's "Revenue or %" column there (PLT-6329). Name and description are edited here
   * and nowhere else (PLT-6331).
   */
  segmentEditorLayout?: boolean;
  /**
   * Attributes cascading into this product from its parent segment(s) (PLT-5695), used to show the
   * read-only "Inherited from parent segment" hint at the top of the attribute tabs. The section
   * editors only expose the product's own values, so this is the only place a user sees inherited
   * ones inside the modal (PLT-5799). Omitted by callers that don't resolve inheritance.
   */
  inheritedIpr?: Partial<IPRAttributes>;
}

export function ProductEditDialog({
  onClose,
  product,
  onSave,
  mode,
  companyId,
  segmentEditorLayout = false,
  inheritedIpr,
}: ProductEditDialogProps) {
  const [editedProduct, setEditedProduct] = useState<PiuProductState>(product);
  // The tab the user picked, or `null` while they haven't picked one. The active tab is derived
  // from this below rather than seeded into state: the subscription query behind `visibleTabs`
  // resolves after first paint, so a state seeded from the initial (access-less) tab set would
  // strand an Enterprise user on the wrong tab once it arrives.
  const [selectedTab, setSelectedTab] = useState<ProductEditDialogTab | null>(null);
  const isTeam = useIsTeam();
  const tabGuidance = useTabGuidance();
  const hasProductAttributeAccess = useHasProductAttributeAccess(companyId);

  const mergedIpr = getMergedIpr({ ipr: editedProduct.ipr, manualIprEdits: editedProduct.manualIprEdits });
  // Warnings judge what the results will use, so values inherited from a parent segment count too:
  // two inherited industries plus two of the product's own is four.
  const attributeWarnings = getAttributeWarnings(
    inheritedIpr ? mergeIprAttributes(inheritedIpr, mergedIpr) : mergedIpr,
  );

  const updateProduct = (updates: Partial<PiuProductState>) => {
    setEditedProduct(prev => ({ ...prev, ...updates }));
  };

  const updateProductIPR = (updates: Partial<IPRAttributes>) => {
    setEditedProduct(prev => ({
      ...prev,
      manualIprEdits: { ...prev.manualIprEdits, ...updates },
    }));
  };

  const attributesTitle = (
    <>
      Attributes <CounterLabel scheme="primary">{getAttributesCount(mergedIpr)}</CounterLabel>
    </>
  ) as unknown as string;

  const advancedTitle = (
    <>
      Advanced attributes <CounterLabel scheme="primary">{getAdvancedAttributesCount(mergedIpr)}</CounterLabel>
    </>
  ) as unknown as string;

  const matchingTitle = (<>Matching</>) as unknown as string;

  const visibleTabs = getVisibleProductEditDialogTabs({
    segmentEditorLayout,
    hasAttributeAccess: hasProductAttributeAccess,
    isTeam,
  });
  // The active tab is whatever the user picked, as long as it is still on offer; otherwise the
  // first tab of the current set. Nothing outside `visibleTabs` can ever be rendered, which is
  // what keeps the attribute sections behind the attribute rule.
  const dialogState = selectedTab !== null && visibleTabs.includes(selectedTab) ? selectedTab : visibleTabs[0];
  const isSegment = product.isSegment === true;
  const tabLabel = (state: ProductEditDialogTab): string => {
    switch (state) {
      case ProductEditDialogTab.BASICS:
        return 'Basic information';
      case ProductEditDialogTab.ATTRIBUTES:
        return attributesTitle;
      case ProductEditDialogTab.ADVANCED:
        return advancedTitle;
      case ProductEditDialogTab.MATCHING:
        return matchingTitle;
    }
  };

  return (
    <Dialog returnFocusRef={{ current: null }} onClose={onClose} title={isSegment ? 'Edit segment' : 'Edit product'}>
      <Dialog.Body sx={{ paddingTop: '0px', paddingBottom: '25px', paddingLeft: '0px', paddingRight: '0px' }}>
        {mode === 'edit' && visibleTabs.length > 1 && (
          <SegmentedControl
            onChange={index => {
              setSelectedTab(visibleTabs[index]);
            }}
          >
            {visibleTabs.map(tab => (
              <SegmentedControl.Button key={tab} selected={dialogState === tab} {...tabGuidance.buttonProps(tab)}>
                {tabGuidance.renderLabel(tab, tabLabel(tab)) as string}
              </SegmentedControl.Button>
            ))}
          </SegmentedControl>
        )}
        <Main>
          {dialogState === ProductEditDialogTab.BASICS && (
            <BasicProperties
              id={editedProduct.id}
              isSegment={isSegment}
              title={editedProduct.title}
              description={editedProduct.description}
              revenueShare={editedProduct.revenueShare}
              onTitleChange={title => {
                updateProduct({ title });
              }}
              onDescriptionChange={description => {
                updateProduct({ description });
              }}
              onRevenueShareChange={revenueShare => {
                updateProduct({ revenueShare });
              }}
              // In the segment editor `revenueShare` is a free relative weight rather than a
              // fraction of revenue, which this field's percentage handling would misrepresent —
              // revenue is edited in the table's own "Revenue or %" column there (PLT-6329).
              showRevenueShare={!segmentEditorLayout}
            />
          )}
          {dialogState === ProductEditDialogTab.ATTRIBUTES && (
            <>
              <InheritedAttributesHint inheritedIpr={inheritedIpr} ownIpr={mergedIpr} />
              <AttributesSection
                mergedIpr={mergedIpr}
                updateProductIPR={updateProductIPR}
                warnings={attributeWarnings}
              />
            </>
          )}
          {dialogState === ProductEditDialogTab.ADVANCED && (
            <>
              <InheritedAttributesHint inheritedIpr={inheritedIpr} ownIpr={mergedIpr} />
              <AdvancedAttributesSection
                mergedIpr={mergedIpr}
                updateProductIPR={updateProductIPR}
                warnings={attributeWarnings}
              />
            </>
          )}
          {dialogState === ProductEditDialogTab.MATCHING && (
            <MatchingSection
              product={editedProduct}
              onProductUpdate={updatedProduct => {
                setEditedProduct(prev => ({ ...prev, ...updatedProduct }));
              }}
            />
          )}
        </Main>
      </Dialog.Body>
      <Dialog.Footer style={customFooterStyle}>
        <FooterContainer>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onSave(editedProduct);
              trackAppcuesEvent(AppcuesEventId.productEditProductSaveClicked, {
                productId: editedProduct.id,
                productTitle: editedProduct.title,
                savedValues: {
                  title: editedProduct.title,
                  description: editedProduct.description,
                  revenueShare: editedProduct.revenueShare,
                },
              });
            }}
            id={TrackingId.SAVE_PRODUCT_BUTTON}
          >
            Save
          </Button>
        </FooterContainer>
      </Dialog.Footer>
    </Dialog>
  );
}

// the footer spacings seem to be a but off. unclear why this is needed, but this fixes the alignments
// could also consider fixing this globally but lets try first here and maybe solve it globally later
// - JO 29.8.2025
const customFooterStyle = { marginLeft: '-16px', marginRight: '-16px', marginBottom: '-16px' };

const Main = styled.div`
  margin-top: var(--space__lg);
`;

const FooterContainer = styled.div`
  display: flex;
  gap: var(--space__sm);
  align-items: center;
`;
